import { useQuery } from '@tanstack/react-query';
import {
  GlobalPermission,
  SystemPermission_Permission,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';
import { authUserQuery } from '~/shared/api/auth-user.queries';
import { hasAcceptedTerms } from '~/shared/auth/auth-utils';
import { useIsSignedIn } from '~/shared/hooks/useIsSignedIn';
import { UserRole } from '~/shared/models/system/RolesModel';
import { AuthUserModel } from '~/shared/models/auth-user/AuthUserModel';
import { assertIsDefined } from '~/types/assert-type';

export function useAuthUser(): AuthUserModel | undefined {
  const isSignedIn = useIsSignedIn();
  const { data: authUser } = useQuery({
    ...authUserQuery,
    enabled: isSignedIn,
  });
  return authUser ?? undefined;
}

export function useRequiredAuthUser() {
  const authUser = useAuthUser();
  assertIsDefined(authUser);
  return authUser;
}

export const useCanSeeIssuesList = (): boolean => {
  const authUser = useAuthUser();
  const canSeeIssuesList = authUser?.canSeeIssuesList ?? false;
  return canSeeIssuesList;
};

export const useHasPermissionForSystem = (
  requestedUserPermissions:
    | SystemPermission_Permission
    | SystemPermission_Permission[],
  systemId: string,
): boolean => {
  const authUser = useAuthUser();

  const permissions = Array.isArray(requestedUserPermissions)
    ? requestedUserPermissions
    : [requestedUserPermissions];

  return permissions.some(
    (permission) =>
      authUser?.hasPermissionForSystemId(permission, systemId) ?? false,
  );
};

export const useHasGlobalPermission = (
  requestedGlobalPermission: GlobalPermission,
): boolean => {
  const authUser = useAuthUser();

  return authUser?.hasGlobalPermission(requestedGlobalPermission) ?? false;
};
// this hook is deprecated and shouldn't be use for new features, use useHasPermissionForSystem instead
export const useHasRoleForSystem = (
  requestedUserRoles: UserRole | UserRole[],
  systemId: string,
): boolean => {
  const authUser = useAuthUser();

  const roles = Array.isArray(requestedUserRoles)
    ? requestedUserRoles
    : [requestedUserRoles];

  return roles.some(
    (role) => authUser?.hasRoleForSystemId(role, systemId) ?? false,
  );
};

export const useHasAcceptedTerms = () => {
  const authUser = useAuthUser();

  if (!authUser) {
    return false;
  }

  return hasAcceptedTerms(authUser.profile.lastAcceptedTermsAndConditions);
};

export const useCanScanDeviceQRCode = (): boolean => {
  const authUser = useAuthUser();
  return authUser?.canScanDeviceQRCode ?? false;
};
